import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNode, useEditor } from '@splashthat/splash-cms-engine';
import TextEditable from 'common/components/TouchpointEditor/TextEditable';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { getCustomCssClassName } from 'common/components/TouchpointEditor/tools/CustomCssTools/CustomCssTools';

import { DefaultLinkComponent } from './Link.styles';

const BASE_CLASS_NAME = 'Link';

export default function Link({
    meta: { content, href },
    styles: { fontFamily, fontSize, fontColor, fontColorHover },
    selected,
    hovered,
    BeforeIcon,
    dataQa,
    className,
    target,
}) {
    const node = useNode();
    const { theme } = useTouchpointData();
    const fontStyles = useMemo(
        () => ({
            $fontFamily: theme.activeFonts[fontFamily],
            $fontColor: theme.colors[fontColor],
            $fontColorHover: theme.colors[fontColorHover],
        }),
        [fontFamily, fontColor, fontColorHover],
    );

    const editor = useEditor((state) => ({ editing: state.editing }));

    const setContent = useCallback(
        (text) =>
            node.actions.setProp((editedNode) => {
                editedNode.meta.content = text; // eslint-disable-line no-param-reassign
                return editedNode;
            }),
        [],
    );

    const buttonStyles = useMemo(() => ({ $fontSize: fontSize, ...fontStyles }), [fontSize, fontStyles]);
    const customCssClassName = getCustomCssClassName(node);
    const joinedClassNames = [className, customCssClassName || ''].join(' ');

    return (
        <DefaultLinkComponent
            className={joinedClassNames}
            selected={selected}
            hovered={hovered}
            onMouseLeave={node.actions.onMouseLeave()}
            onMouseMove={node.actions.onMouseMove()}
            href={href}
            target={target}
            rel="noopener noreferrer"
            draggable={false}
            onClick={node.actions.onClick()}
            buttonStyles={buttonStyles}
            data-qa={dataQa || `${BASE_CLASS_NAME}--button-container`}
            hasBeforeIcon={!!BeforeIcon}
        >
            {!!BeforeIcon && <BeforeIcon />}
            <TextEditable
                setContent={setContent}
                disabled={!editor.editing || (editor.editing && !selected && !hovered)}
            >
                {content}
            </TextEditable>
        </DefaultLinkComponent>
    );
}

Link.splash = {
    related: {
        settings: React.lazy(() => import('./Link.tools')),
    },
    defaultProps: {
        meta: {},
        styles: {},
    },
};

Link.propTypes = {
    BeforeIcon: PropTypes.func,
    className: PropTypes.string,
    meta: PropTypes.shape({
        content: PropTypes.string.isRequired,
        href: PropTypes.string.isRequired,
    }).isRequired,
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
    styles: PropTypes.shape({
        fontFamily: PropTypes.number,
        fontSize: PropTypes.number,
        fontColor: PropTypes.number,
        fontColorHover: PropTypes.number,
    }).isRequired,
    dataQa: PropTypes.string,
    target: PropTypes.string,
};

Link.defaultProps = {
    BeforeIcon: null,
    className: '',
    dataQa: undefined,
    target: '',
};
